import { EnumStorageTransactionType } from '@Model';
import { FlexRow, useDebouncedValue } from '@synqo/frontend-component-library';
import { useStorageStock } from '../Stock/useStorageStock';
import { cancellationPreview, STOCK_DEBOUNCE_MS, stockEffectPreview, stockPreview } from './previewLine';
import type { StockPreviewValues, StockPreviewMode } from './previewLine';
import { StockQuantity } from '@Components/StorageStock';
import classes from './StockPreviewLine.module.css';

type PreviewLoader = (
    type: EnumStorageTransactionType,
    stockAtInstant: number,
    quantity: number,
) => StockPreviewValues | undefined;

interface IProps {
    storageLocationId: string | undefined;
    materialId: string | undefined;
    avvId: string | undefined;
    occurredAt: string;
    type: EnumStorageTransactionType;
    quantity: number;
    /** The stored booking, so bookings that sort after it at its instant stay out of the stock read. */
    bookingId?: string;
    /** A stored booking is already in the stock read: its effect, or the stock after cancelling it. */
    view?: StockPreviewMode;
}

const WORDINGS: Record<StockPreviewMode, { first: string; second: string }> = {
    booking: { first: 'Bestand vorher', second: 'danach' },
    effect: { first: 'Bestand davor', second: 'danach' },
    cancellation: { first: 'Bestand zum Buchungszeitpunkt', second: 'nach Storno' },
};

const PREVIEWS: Record<StockPreviewMode, PreviewLoader> = {
    booking: stockPreview,
    effect: stockEffectPreview,
    cancellation: cancellationPreview,
};

/** The stock of the booked key at the booking's Zeitpunkt and what it becomes, what a stored booking did to it, or what its cancellation does. */
// @ai-generated 2026-10-23T15:50:00+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function StockPreviewLine({
    storageLocationId,
    materialId,
    avvId,
    occurredAt,
    type,
    quantity,
    bookingId,
    view = 'booking',
}: IProps) {
    const settledAt = useDebouncedValue(occurredAt, STOCK_DEBOUNCE_MS);
    const known = type !== EnumStorageTransactionType.Stocktaking && !!storageLocationId && !!materialId && !!settledAt;
    const stock = useStorageStock({ storageLocationId, validAt: settledAt, bookingId, enabled: known });

    if (!known || stock.isLoading || stock.failure) {
        return null;
    }

    const row = stock.entries.find(
        (entry) => entry.material.foreign_key === materialId && (entry.avv.foreign_key ?? undefined) === avvId,
    );
    const stockAtInstant = row?.quantity ?? 0;
    const preview = PREVIEWS[view](type, stockAtInstant, quantity);

    if (!preview) {
        return undefined;
    }

    const { first, second } = WORDINGS[view];

    return (
        <FlexRow className={classes.preview} gap="xxs" wrap="wrap">
            {first} <StockQuantity value={preview.current} unit="t" /> → {second}{' '}
            <StockQuantity value={preview.after} unit="t" bold />
        </FlexRow>
    );
}
